import React from "react";
import SEO from "../components/SEO";
import LegalPage, { H2, H3, P, UL } from "../components/LegalPage";

/**
 * The operator's own booking terms, taken from the terms section of the tour
 * proposal rather than written from a template. The cancellation percentages,
 * the deposit split, the flight contingency and the altitude disclaimer are all
 * theirs — this page exists so a guest can read them before paying rather than
 * after, which is the whole point of publishing them.
 */

export default function TermsPage() {
  return (
    <>
      <SEO
        title="Booking Terms | D, Trio Trek"
        description="Booking and payment, the Skardu flight contingency, cancellation charges, and the altitude and liability terms for D, Trio Trek expeditions."
      />
      <LegalPage title="Booking Terms & Conditions" updated="August 2026">
        <P>
          D, Trio Trek — Gilgit-Baltistan valley expeditions. By booking a tour
          with us you confirm that you have read, understood and agreed to the
          terms below.
        </P>

        <H2>1. Booking and payment</H2>
        <UL items={[
          "A non-refundable 50% advance of the total tour cost is required at booking, to reserve domestic flights, hotels and 4x4 vehicles.",
          "The remaining 50% is due at least 14 days before departure — or on arrival in Islamabad before the flight, if agreed in writing.",
          "A booking is confirmed only when we issue a formal digital invoice or written confirmation.",
        ]} />

        <H2>2. Skardu flights</H2>
        <P>
          Flights between Islamabad and Skardu operate under visual flight rules
          and depend on mountain weather. This is the single most common
          disruption to a trip here, so it is worth reading twice.
        </P>
        <UL items={[
          "We are not responsible for delays, reschedules or cancellations by the airline — weather, technical or airspace related.",
          "If a flight is cancelled you may wait for the next available seat, or travel by private road transport on the Karakoram Highway / Hazara Motorway, which we will arrange. Any material difference between the airfare refund and the road cost is adjusted or billed accordingly.",
          "Additional nights, meals or incidental costs in Islamabad or Skardu caused by flight disruption are the guest's responsibility.",
        ]} />

        <H2>3. Cancellation and refunds</H2>
        <P>
          Cancellations must be sent in writing, by email or official WhatsApp.
          The charge depends on how much notice we have:
        </P>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm border-t border-primary-200">
            <thead>
              <tr className="text-left">
                <th className="py-3 pr-4 font-semibold text-primary-900 border-b border-primary-200">
                  Notice before departure
                </th>
                <th className="py-3 font-semibold text-primary-900 border-b border-primary-200">
                  Refund
                </th>
              </tr>
            </thead>
            <tbody className="text-primary-600">
              {[
                ["30+ days", "80% of the total tour cost, after non-refundable airfare deductions"],
                ["15–29 days", "50% of the total tour cost, after non-refundable airfare deductions"],
                ["7–14 days", "25% of the total tour cost"],
                ["Under 7 days, or no-show", "No refund"],
              ].map(([notice, refund]) => (
                <tr key={notice}>
                  <td className="py-3 pr-4 border-b border-primary-100 align-top whitespace-nowrap">
                    {notice}
                  </td>
                  <td className="py-3 border-b border-primary-100">{refund}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <P>
          Domestic flight tickets follow the airline's own refund and penalty
          rules. Whatever portion the airline will not refund is deducted before
          your refund is calculated.
        </P>

        <H2>4. Changes to the itinerary</H2>
        <UL items={[
          "High-altitude terrain and road conditions — the Deosai crossing and Basho Valley especially — change without warning. We may alter the daily itinerary, substitute hotels or change routes in the interest of safety.",
          "No refund or credit is issued for unused hotel nights, meals, transport or sights missed through personal choice, late arrival or early departure.",
        ]} />

        <H2>5. Health, altitude and fitness</H2>
        <UL items={[
          "Skardu, Shigar, Khaplu and Deosai sit between roughly 2,200 m and over 4,000 m. You are responsible for assessing your own fitness before booking.",
          "Tell us about medical conditions, respiratory problems or severe allergies before departure.",
          "Our guides take acclimatisation precautions, but we are not medically liable for altitude-related illness.",
        ]} />

        <H2>6. Insurance and liability</H2>
        <UL items={[
          "We strongly advise personal travel insurance covering medical emergencies, cancellation, lost luggage and emergency high-altitude evacuation.",
          "We are not liable for loss, theft or damage to personal luggage, electronics or valuables during the tour.",
          "We are not liable for failure to perform where it is caused by acts of God, natural disasters, landslides, avalanches, civil unrest, strikes, government closures or airline shutdowns.",
        ]} />

        <H2>7. What we ask of you</H2>
        <UL items={[
          "Respect local customs and leave-no-trace guidelines throughout Baltistan.",
          "Carry government photo identification at all times — CNIC for Pakistani citizens, passport and valid visa for foreign nationals.",
          "We may end the participation of anyone whose conduct is disruptive, illegal or hazardous to the group's safety, without refund.",
        ]} />

        <H2>Questions</H2>
        <H3>Bookings, payments or adjustments to these terms</H3>
        <P>
          Email{" "}
          <a href="mailto:dtriotrek@gmail.com" className="underline">dtriotrek@gmail.com</a>{" "}
          or message{" "}
          <a href="https://wa.me/923102329666" target="_blank" rel="noreferrer" className="underline">
            +92 310 2329666
          </a>{" "}
          on WhatsApp. Office: Skardu and Islamabad, Pakistan.
        </P>
      </LegalPage>
    </>
  );
}
